import { APIService } from "./api.service";
import {
  CalculationDto,
  Calculation,
  convertFromServer,
  prepareForServer,
} from "@/models/Calculation";
import config from "@/config/config";
import { Federation } from "@/models/Federation";
import {
  CalculationFields,
  ContingentOffset,
  CalculationResponse,
  InvoiceData,
  OrderLineType,
  YearRange,
} from "@/types";
import { AxiosResponse } from "axios";
import { InvoiceDataUtils } from "@/types/invoice-data";
import { toQueryString } from "@/utils";
import { ContingentOffsetDataUtils } from "@/types/contingent-offset";
import ContingentResponse from "@/types/responses/contingent-response";

const ApiService = new APIService(config.api.url);

type Options = {
  mock: boolean;
  recalculate: boolean;
  usePercentage?: boolean;
};

export default {
  async GetFederations(): Promise<Array<Federation>> {
    const response = await ApiService.Get("/api/federation");
    return response.data;
  },
  async SaveCalculation(calculation: Calculation): Promise<AxiosResponse<any>> {
    return ApiService.post("/api/calculation", prepareForServer(calculation));
  },
  async GetCalculation(
    nhoNumber: number,
    year: string,
    calculationType: string,
    membershipType: number
  ): Promise<Calculation> {
    const queryString = toQueryString({
      nhoNumber: nhoNumber,
      year: year,
      type: calculationType,
      membershipType: membershipType,
    });
    const response = await ApiService.Get(
      `api/calculation/byyear?${queryString}`
    );
    return response.data;
  },
  async GetCalculations(): Promise<Array<Calculation>> {
    const response = await ApiService.Get("/api/calculation/all");
    return (response.data as Array<CalculationDto>).map((x) =>
      convertFromServer(x)
    );
  },
  async calculateContingent(
    calculationFields: CalculationFields
  ): Promise<CalculationResponse> {
    const crmQueryString = toQueryString({
      year: calculationFields.calculationYear,
      nhoNumber: calculationFields.nhoNumber,
    });
    const endpoint = calculationFields.useCrmYearReport
      ? `/api/contingent/calculate-crm-report?${crmQueryString}`
      : "/api/contingent";

    const response = await ApiService.post<CalculationFields>(
      endpoint,
      calculationFields
    );
    return response.data;
  },
  async copyCalculations(yearRange: YearRange): Promise<{
    copiedCalculations: number;
    copiedInvoiceData: number;
  }> {
    const response = await ApiService.post("/api/calculation/copy", yearRange);
    return response.data;
  },
  async getInvoiceData(): Promise<InvoiceData[]> {
    const response = await ApiService.Get("/api/InvoiceData/all");
    return response.data;
  },
  async addOrUpdateInvoiceData(item: InvoiceData): Promise<InvoiceData> {
    const response = await ApiService.post(
      "/api/InvoiceData",
      InvoiceDataUtils.prepareForServer(item)
    );
    return response.data;
  },
  async deleteInvoiceData(
    nhoNumber: number,
    orderLineType: OrderLineType
  ): Promise<string> {
    const queryString = toQueryString({
      nhoNumber: nhoNumber,
      orderLineType: orderLineType,
    });
    const urlPath = `/api/InvoiceData/delete?${queryString}`;
    const response = await ApiService.delete(urlPath);
    return response.data;
  },
  async getContingentOffsets(
    year: number,
    offset: number | string,
    options: Options = {
      mock: false,
      recalculate: false,
      usePercentage: false,
    }
  ): Promise<ContingentOffset[]> {
    if (options.mock) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(ContingentOffsetDataUtils.randomList());
        }, 1000);
      });
    }
    const response = await ApiService.post(
      `/api/ContingentOffset/above?year=${year}&offset=${
        offset == "" ? 0 : offset
      }&usePercentage=${options.usePercentage}&recalculate=${
        options.recalculate
      }`,
      null
    );
    return response.data;
  },
  async getOffsetDistributionPerMo(
    year: number,
    offset: number | string,
    options: Options = {
      mock: false,
      recalculate: false,
      usePercentage: false,
    }
  ): Promise<string> {
    const response = await ApiService.Get(
      `/api/ContingentOffset/offsets-per-mo?year=${year}&offset=${
        offset == "" ? 0 : offset
      }&usePercentage=${options.usePercentage}&recalculate=${
        options.recalculate
      }`
    );
    return response.data;
  },
  async recalculateContingentOffset(
    year: number,
    nhoNumber: number | null
  ): Promise<ContingentOffset> {
    const response = await ApiService.post(
      `/api/ContingentOffset/recalculate?nhoNumber=${nhoNumber}&year=${year}`,
      null
    );
    return response.data;
  },
  async recalculateContingentOffsets(
    year: number,
    nhoNumbers: number[]
  ): Promise<ContingentOffset[]> {
    const response = await ApiService.post(
      `/api/ContingentOffset/recalculate-multiple?year=${year}`,
      nhoNumbers
    );
    return response.data;
  },
  async getContingent(
    year: number,
    nhoNumber: number
  ): Promise<ContingentResponse> {
    const response = await ApiService.Get(
      `/api/contingent?year=${year}&nhoNumber=${nhoNumber}`
    );
    return response.data;
  },
};
